import {orderBy} from 'lodash';
import React, {FC, useEffect} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {TopBarDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/topBarDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {ArticleCard} from '../../../../../libs/shared/ui/src/components/articleCard/articleCard';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ColorStyles} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import {ProductUpdate, ProductUpdateCategory} from './productUpdatesHelpers';

/*
 * Props.
 */

interface SettingsProductUpdatesWidgetProps {
  productUpdates: ReadonlyArray<ProductUpdate>;
  onContentSeen: () => void;
}

const StyledContainerDiv = styled.div`
  width: 600px;
  display: flex;
  flex-direction: column;
  padding: 24px 30px 30px 30px;
  overflow: hidden;
  max-height: 598px;
`;

const StyledHeaderDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 16px;
  height: 30px;
`;

const StyledHeaderText = styled.h3`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.large};
  line-height: 19px;
  color: ${(p) => p.theme.greys.shade90};
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledArticlesContainer = styled.div`
  overflow-y: auto;
`;

/*
 * Component.
 */

export const SettingsProductUpdatesWidget: FC<SettingsProductUpdatesWidgetProps> = (props) => {
  const {productUpdates, onContentSeen} = props;

  const theme = useUserTheme();
  const {trackDataEvent} = useDataEventTracking();
  const sortedProductUpdates = orderBy(productUpdates, ['weight', 'timestamp'], ['asc', 'desc']);

  useInitialEffect(() => {
    trackDataEvent(DataEventActionsEnum.VIEW, TopBarDataEventObject.HELP_WIDGET_PRODUCT_UPDATES_TAB);
  });

  useEffect(() => {
    onContentSeen();
  }, [
    // We want to call onContentSeen every time productUpdates is updated while this component is mounted.
    productUpdates,
    onContentSeen,
  ]);

  return (
    <StyledContainerDiv>
      <StyledHeaderDiv>
        <StyledHeaderText>Discover the latest updates</StyledHeaderText>
        <Button
          rightIcon="externalLink"
          variant={ButtonVariantsEnum.LINK}
          size={VisualSizesEnum.MEDIUM}
          onClick={() => {
            trackDataEvent(
              DataEventActionsEnum.CLICK,
              TopBarDataEventObject.HELP_WIDGET_PRODUCT_UPDATES_SEE_MORE,
            );
            openInBrowser('https://community.front.com/announcements-63');
          }}
        >
          See more
        </Button>
      </StyledHeaderDiv>
      <StyledArticlesContainer>
        {sortedProductUpdates.map((productUpdate) => {
          const {articlePill, articlePillColor} = computeArticlePillProps(productUpdate.category, theme);

          return (
            <ArticleCard
              key={productUpdate.id}
              link={productUpdate.linkUrl}
              title={productUpdate.title}
              titleMaximumLines={1}
              titleMaximumWidth={424}
              description={productUpdate.body}
              descriptionMaximumWidth={424}
              articlePill={articlePill}
              articlePillColor={articlePillColor}
              onArticleClick={() => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  TopBarDataEventObject.HELP_WIDGET_PRODUCT_UPDATES_OPEN_ARTICLE,
                  {
                    articleLinkUrl: productUpdate.linkUrl,
                    articleTitle: productUpdate.title,
                  },
                );
                openInBrowser(productUpdate.linkUrl);
              }}
            />
          );
        })}
      </StyledArticlesContainer>
    </StyledContainerDiv>
  );
};

function computeArticlePillProps(category: ProductUpdateCategory, theme: ColorStyles) {
  switch (category) {
    case ProductUpdateCategory.ANNOUNCEMENT:
      return {
        articlePill: (
          <Trans id="settings-product-updates-widget-announcement-category-pill">Announcement</Trans>
        ),
        articlePillColor: PaletteColorsEnum.GREEN,
      };
    case ProductUpdateCategory.EVENT:
      return {
        articlePill: <Trans id="settings-product-updates-widget-event-category-pill">Event</Trans>,
        articlePillColor: PaletteColorsEnum.BLUE,
      };
    case ProductUpdateCategory.FEATURE:
      return {
        articlePill: <Trans id="settings-product-updates-widget-feature-category-pill">Feature</Trans>,
        articlePillColor: PaletteColorsEnum.ORANGE,
      };
    case ProductUpdateCategory.RESOURCE:
      return {
        articlePill: <Trans id="settings-product-updates-widget-resource-category-pill">Resource</Trans>,
        articlePillColor: PaletteColorsEnum.PURPLE,
      };
    case ProductUpdateCategory.TRAINING:
      return {
        articlePill: <Trans id="settings-product-updates-widget-training-category-pill">Training</Trans>,
        articlePillColor: PaletteColorsEnum.YELLOW,
      };
    default:
      throw new UnreachableError(category);
  }
}
